import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyJapaneseMixin from '@/user/mixins/StudyJapaneseMixin';
import StudyWorkMixin, { PEN_COLOR, TEXT_COLOR } from '@/user/mixins/StudyWorkMixin';
import WindowAdjustMixin from '@/user/mixins/WindowAdjustMixin';
import LineData from '@/user/pages/authorized/study/japanese/work/JapaneseLineData';
import ReplyView from '@/user/pages/authorized/study/japanese/work/JapaneseReplyView';
import WordData from '@/user/pages/authorized/study/japanese/work/JapaneseWordData';

import CanvasTextUtility from '@/classes/common/CanvasTextUtility';
import UserWindowSizeUtility from '@/classes/common/UserWindowSizeUtility';
import UserPathData from '@/classes/data/UserPathData';


interface InputAssistanceStyle {
  display: string;
  left: string;
  top: string;
  width: string;
  height: string;
}

/** 行頭禁則文字 */
export const HEAD_NG_CHAR = '、。」）』＞ー　ァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎ？！';

/** 行末禁則文字 */
export const TAIL_NG_CHAR = '「（『＜';

/** 国語問題用フォントサイズ */
const DEFALUT_TEXT_SIZE = 12;

/** デフォルト余白サイズ */
const DEFAULT_MARGIN = DEFALUT_TEXT_SIZE;     // 文字サイズ

/** デフォルト行間 */
const DEFAULT_LINE_SPACE = DEFALUT_TEXT_SIZE / 3; // 文字サイズの1/3

/** チュートリアル入力補助問題設定 */
const TUTORIAL_INPUT_ASSISTANCE = {
  isQuestion: false,
  sectionNo: 1,
  questionIndex: 0,
  charIndex: 5
};


export default mixins(
  RoutesMixin,
  StudyJapaneseMixin,
  StudyWorkMixin,
  WindowAdjustMixin
).extend({

  name: 'JapaneseWork',

  props: {
    isQuestion: {
      type: Boolean,
      required: true,
    },
    isFontLarge: {
      type: Boolean,
      required: true,
    },
    clearSwitch: {
      type: Boolean,
      required: true,
    },
  },


  data(): {
    enableLeftScroll: boolean;
    enableRightScroll: boolean;
    isScrollingLeft: boolean;
    isScrollingRight: boolean;
    inputAssistanceStyle: InputAssistanceStyle;
    // 以下、private
    lines: LineData[];  // 問題テキスト
    pathDatas: UserPathData[];  // 筆跡情報
    scrollWidth: number;  // スクロールを含んだ画面サイズ
    scrollPos: number;  // スクロール量（0のとき、テキストの右端が表示されている）
    totalWordCount: number;  // 文字数カウント
    animationInterval: number

  } {
    return {
      enableLeftScroll: false,
      enableRightScroll: false,
      isScrollingLeft: false,
      isScrollingRight: false,
      inputAssistanceStyle: {
        display: 'none',
        left:    '0',
        top:     '0',
        width:   '0',
        height:  '0',
      },
      lines: [],
      pathDatas: [],
      scrollWidth: 0,
      scrollPos: 0,
      totalWordCount: 0,
      animationInterval: -1,
    };
  },

  computed: {
    rootCssClass(): string {
      return `study${this.isQuestion ? 'Answer' : 'Question'}Box`;
    },
    canvasId(): string {
      return `study${this.isQuestion ? 'Answer' : 'Question'}Canvas`;
    },
    margin(): number {
      const margin = UserWindowSizeUtility.calculateRatio(DEFAULT_MARGIN);
      if (!this.isFontLarge) return margin;
      return margin * 1.5;
    },
    lineSpace(): number {
      const lineSpace = UserWindowSizeUtility.calculateRatio(DEFAULT_LINE_SPACE);
      if (!this.isFontLarge) return lineSpace;
      return lineSpace * 1.5;
    },
    fontSize(): number {
      const fontSize = UserWindowSizeUtility.fontSize();
      if (!this.isFontLarge) return fontSize;
      return fontSize * 1.5;
    },
    focusReplyView(): ReplyView | null {
      if (this.focusReplyViewIndex === -1) return null;
      return this.replys[this.focusReplyViewIndex];
    },
    requiredAssistance(): boolean {
      return TUTORIAL_INPUT_ASSISTANCE.isQuestion === this.isQuestion &&
        TUTORIAL_INPUT_ASSISTANCE.sectionNo === this.studySectionNo &&
        TUTORIAL_INPUT_ASSISTANCE.questionIndex === this.questionIndex;
    },
  },

  created(): void {
    // 入力補助設定
    this.$watch(
      () => [
        this.requiredAssistance,
        this.prejudgeReplys
      ],
      () => {
        const display = this.requiredAssistance && !this.prejudgeReplys
          ? 'block'
          : 'none';
        this.inputAssistanceStyle.display = display;
      }
    );
  },

  mounted(): void {
    this.initializeCanvas();
    this.setCanvasSize();
    this.animationInterval = setInterval(() => this.$_animation(), 100);
  },

  destroyed(): void {
    clearInterval(this.animationInterval);
  },

  watch: {
    // StudyMixin.computed を監視
    questionIndex: {
      handler(questionIndex: number): void {
        if (questionIndex === -1) {
          this.$_clearCanvas();
          return;
        }
        this.$_setQuestion();
      }
    },
    isFontLarge: {
      handler(): void {
        const inputs = this.isQuestion ? this.replys.map((rv) => rv.getText()) : [];
        this.$_setQuestion(inputs);
      }
    },
    // 全消去
    clearSwitch(): void {
      this.$_setQuestion();
    },
  },

  methods: {
    /**
     * 画面サイズ変更検知時のハンドリング
     */
    onChangeWindowSizeHandler(): void {
      // 呼び出し側で処理を実装
      this.setCanvasSize();
      const inputs = this.isQuestion ? this.replys.map((rv) => rv.getText()) : [];
      this.$_setQuestion(inputs);
    },

    /**
     * StudyWorkMixinを上書き
     */
    onTouchEnd(): void {
      // 手書き中でない場合はreturn
      if (!this.isHandWriting) return;

      // 選択した文字列を回答欄に反映
      this.$_onPoint();
      const touchText = this.$_getTouchText();
      if (touchText.length > 0) this.$_setReplyText(touchText);

      // 次の手書きへの準備
      this.pathData.clear();
      this.isHandWriting = false;
    },

    onScrollingLeft(isScrolling: boolean): void {
      this.isScrollingLeft = isScrolling;
    },

    onScrollingRight(isScrolling: boolean): void {
      this.isScrollingRight = isScrolling;
    },

    /**
     * 回答文字列の設定（タッチ用）
     *
     * @param str タッチ位置文字列
     */
    $_setReplyText(str: string): void {
      if (this.focusReplyView === null) return;
      this.focusReplyView.setText(str);

      // 次の未解答欄にフォーカスをセットする
      const index = this.$_getNextFocusIndex();
      if (index > -1) this.setFocus(index);
    },

    /**
     * 問題をセットします。
     */
    $_setQuestion(inputs?: string[]): void {

      if (this.questionIndex === -1) return; // Indexが初期値の場合はreturn

      // パスの初期化
      this.pathDatas = [];

      // テキストの初期化
      this.lines = [];

      // スクロール位置の初期化
      this.scrollPos = 0;

      // 問題の設定
      const question = this.question!;
      const questionNo = question!.questionNo;
      const right = question.right;
      const text  = (this.isQuestion ? question.q : question.a).replace(/\r/g, '').replace(/\\n/g, '\n');

      // 入力欄のサイズ
      if (this.isQuestion) this.refreshReplys(right.split(';').length);
      const rights = this.isQuestion ? right.split(';') : [];

      let inputCount = 0;
      const texts = text.split('\n');

      for (let i = 0; i < texts.length; i++) {
        const tmpText = texts[i];
        let lineData = new LineData(this.fontSize, this.margin);
        let lineFlag = false;

        let index = 0;
        while (true) {
          if (tmpText.length === index) break;
          const c = tmpText.charAt(index);

          if (c === '[') {
            lineFlag = true;
            index++;
            continue;
          } else if (c === ']') {
            lineFlag = false;
            index++;
            continue;
          }

          let word = '';
          if (c === '□') {
            word = '□';
            while (true) {
              index++;
              if (tmpText.length === index) break;
              if (tmpText.charAt(index) !== '□') {
                break;
              }
              word += '□';
            }
          } else if (c === '/') {
            let nextc = '';
            while (true) {
              index++;
              if (tmpText.length === index) {
                const error = 'sectionNO:' + this.studySectionNo
                            + ', questionNO:' + questionNo
                            + ': start / not () at ' + word;
                throw new Error(error);
              }
              nextc = tmpText.charAt(index);
              word += nextc;
              if (nextc === ')') {
                index++;
                break;
              }
            }
          } else if (c === '<') {
            word = '<';
            while (true) {
              index++;
              if (tmpText.length === index) {
                const error = 'sectionNO:' + this.studySectionNo
                            + ', questionNO:' + questionNo
                            + ': start < not > at ' + word;
                throw new Error(error);
              }
              const nextc = tmpText.charAt(index);
              word += nextc;
              if (nextc === '>') {
                index++;
                break;
              }
            }
          } else {
            word += c;
            let nextc = '';
            if (index < tmpText.length - 1) nextc = tmpText.charAt(index + 1);
            if (nextc === '(') {
              while (true) {
                index++;
                if (tmpText.length === index) {
                  const error = 'sectionNO:' + this.studySectionNo
                              + ', questionNO:' + questionNo
                              + ': start ( not ) at ' + word;
                  throw new Error(error);
                }
                nextc = tmpText.charAt(index);
                word += nextc;
                if (nextc === ')') {
                  index++;
                  break;
                }
              }
            } else {
              index++;
            }
          }

          const wordData = new WordData(
            this.canvasContext,
            word,
            lineFlag,
            !this.isFontLarge,
            this.fontSize,
            question.qaImages  // TODO
          );
          if (wordData.reply !== null) {
            // 設問数が解答よりも多い場合
            if (inputCount === rights.length) {
              const error = 'sectionNO:' + this.studySectionNo
                          + ', questionNO:' + questionNo
                          + ': inputCount != rights.length';
              throw new Error(error);
            }
            this.replys[inputCount] = wordData.reply;
            inputCount++;
          }
          if (!lineData.add(wordData, this.canvas.height)) {
            const lastWordDataList = lineData.checkLastWord(wordData);
            this.lines.push(lineData);
            lineData = new LineData(this.fontSize, this.margin);
            lastWordDataList.forEach((wd) => lineData.add(wd, this.canvas.height));
            lineData.add(wordData, this.canvas.height);
          }
        }
        // 改行コードを追加
        lineData.addLF();
        this.lines.push(lineData);
      }

      // 設問数が解答数よりも少ない場合
      if (inputCount < rights.length) {
        const error = 'sectionNO:' + this.studySectionNo
                    + ', questionNO:' + questionNo
                    + ': inputCount != rights.length';
        throw new Error(error);
      }

      // 文字入力設定
      for (let i = 0; i < inputCount; i++) {
        // 文字認識の設定
        this.replys[i].setRight(rights[i]);
        // リサイズ時は回答情報を復元
        if (inputs !== undefined) this.replys[i].setText(inputs[i]);
      }

      // 最初の入力にフォーカスをあてる
      if (this.isQuestion) this.setFocus(0);

      // スクロール幅の決定
      this.$_setScrollWidth();

      // 再描画
      this.$_onPaint();
    },

    /**
     * スクロール幅を設定します。
     */
    $_setScrollWidth(): void {

      // スクロール幅の決定
      this.scrollWidth = this.margin * 2;
      for (let i = 0; i < this.lines.length; i++) {
        const lineData = this.lines[i];
        this.scrollWidth += lineData.w;
      }
      this.scrollWidth += this.lineSpace * (this.lines.length - 1);

      // もしスクロール幅が画面サイズよりも小さいときは、サイズを合わせる
      if (this.scrollWidth < this.canvas.width) this.scrollWidth = this.canvas.width;

      // スクロール位置の初期化
      this.scrollPos = 0;
    },

    /**
     * 描画を行います。
     */
    $_onPaint(): void {
      // キャンバスクリア
      this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // 問題の描画
      this.$_drawQuestion();

      // 筆跡の描画
      this.canvasContext.save();
      this.canvasContext.translate(-this.scrollWidth + this.canvas.width + this.scrollPos, 0);
      this.$_drawPath();
      this.canvasContext.restore();

      // 回答欄を最前面にももってたいので筆跡の後に描画
      if (this.isQuestion) this.$_drawReplyView();
    },

    /**
     * 筆跡の描画
     */
    $_drawPath(): void {

      this.canvasContext.lineWidth = UserWindowSizeUtility.penSize();
      this.canvasContext.strokeStyle = PEN_COLOR;

      this.canvasContext.beginPath();
      _.each(
        this.pathDatas,
        pathData => {
          for (let i = 0; i < pathData.pointCount; i++) {
            const x = pathData.pointX[i];
            const y = pathData.pointY[i];

            // 始点の場合
            if (i === 0) {
              this.canvasContext.moveTo(x, y);
            }
            // 始点でない場合
            else {
              this.canvasContext.lineTo(x, y);

            }
          }
        }
      );
      this.canvasContext.stroke();
      this.canvasContext.closePath();
    },

    /**
     * 問題を描画します
     */
    $_drawQuestion(): void {

      // 問題が存在しないときは終了
      if (this.questionIndex < 0) return;

      this.canvasContext.lineWidth = UserWindowSizeUtility.penSize();
      this.canvasContext.strokeStyle = TEXT_COLOR;
      this.totalWordCount = 0;

      let posR = this.margin; // 右端からの座標
      for (let i = 0; i < this.lines.length; i++) {
        const lineData = this.lines[i];
        let posY = this.margin; // 上からの座標
        let lastWord = ''; // 1つ前の単語
        for (let j = 0; j < lineData.wordList.length; j++) {
          const wordData = lineData.wordList[j];
          if (wordData.reply !== null) {
            // 入力位置調整
            const editPosY = posY + UserWindowSizeUtility.calculateRatio(2);
            let editPosX = this.canvas.width - posR - wordData.reply.width + this.scrollPos - UserWindowSizeUtility.calculateRatio(2);
            if (this.isFontLarge) editPosX -= UserWindowSizeUtility.calculateRatio(4);
            wordData.reply.setCoordinate(editPosX, editPosY);
          } else if (wordData.image !== null) {
            // 画像描画、読込を待って処理
            const x = this.canvas.width - (posR + wordData.w) + this.scrollPos;
            this.canvasContext.drawImage(wordData.image, x, posY, wordData.w, wordData.h);
          } else {
            // 表示領域内
            if (posR - this.scrollPos >= 0 && posR + lineData.w - this.scrollPos <= this.canvas.width) {
              // 文字列描画
              const HALF_CHAR = '、。」）』';
              if (HALF_CHAR.indexOf(lastWord) !== -1 && HEAD_NG_CHAR.indexOf(wordData.word) !== -1) {
                this.$_drawWord(wordData, posR, posY - wordData.h / 2 - 1); // 最後の「-1」は、単なる調整
              } else {
                this.$_drawWord(wordData, posR, posY);
              }
            }
          }
          posY += wordData.h;
          lastWord = wordData.word;
          this.totalWordCount += wordData.word.length;
        }
        posR += (lineData.w + this.lineSpace);
      }
    },

    /**
     * 文字列を描画します
     *
     * @param canvas Canvas
     * @param wordData WordData
     * @param posR 右からの座標
     * @param posY 上からの座標
     */
    $_drawWord(wordData: WordData, posR: number, posY: number): void {

      const wordCount = wordData.word.length;
      const rubiCount = wordData.rubi.length;

      const tw = this.fontSize;
      const th = this.fontSize;

      // 本文
      let x         = this.canvas.width - (posR + tw * 3 / 2) + this.scrollPos;
      let startY    = posY + (wordData.h - th * wordCount) / 2;
      let charIndex = this.totalWordCount;
      this.canvasContext.font = UserWindowSizeUtility.canvasFont(th, false);

      for (let i = 0; i < wordCount; i++) {
        charIndex++;

        // 文字の縦描画
        const word = wordData.word.charAt(i);
        const y    = startY + th * (i + 1);
        CanvasTextUtility.drawVertical(this.canvasContext, word, x, y);

        // 入力補助位置設定
        if (this.requiredAssistance && TUTORIAL_INPUT_ASSISTANCE.charIndex === charIndex) {
          this.inputAssistanceStyle.left   = x + 'px';
          this.inputAssistanceStyle.top    = y - th * 5 / 6 + 'px';
          this.inputAssistanceStyle.width  = th + 'px';
          this.inputAssistanceStyle.height = th + 'px';
        }
      }

      // ライン
      if (wordData.lineFlag) {
        this.canvasContext.fillRect(x + tw - 1, posY, UserWindowSizeUtility.penSize(), wordData.h);
      }

      // ルビ
      let fsize = th / 2;

      // ルビが①～⑳の場合は特殊処理
      if (rubiCount === 1 && wordData.rubi.charAt(0) >= '①' && wordData.rubi.charAt(0) <= '⑳') {
        fsize = th * 3 / 4;
      }

      x = this.canvas.width - (posR + tw / 2) + this.scrollPos;
      x += UserWindowSizeUtility.penSize() - 1; // ラインを引くことを考慮して少し補正している
      startY = posY + (wordData.h - fsize * rubiCount) / 2;
      startY += fsize / 4; // 文字の描画高さは、フォントで指定した数値と少し違う（正確に調べるには FontMetrics を使う）ので、補正している
      this.canvasContext.font = UserWindowSizeUtility.canvasFont(fsize, false);
      for (let i = 0; i < rubiCount; i++) {
        CanvasTextUtility.drawVertical(this.canvasContext, wordData.rubi.charAt(i), x, startY + fsize * (i + 1));
      }
    },

    /**
     * 回答欄を描画
     */
    $_drawReplyView(): void {
      _.each(
        this.replys,
        reply => reply.onDraw()
      );
    },

    /**
     * 筆跡処理
     */
    $_onPoint(): void {
      // 解答側の場合は筆跡開始位置をチェックして、回答欄の場合は回答欄にフォーカスを当てる
      if (this.isQuestion) {
        for (let i = 0; i < this.replys.length; i++) {
          if (this.replys[i].hitTest(this.pathData.pointX[0], this.pathData.pointY[0])) {
            this.setFocus(i);
            return;
          }
        }
      }

      // 筆跡データをためる
      const convertedPathData = _.cloneDeep(this.pathData);
      convertedPathData.convert(this.scrollWidth - this.canvas.width - this.scrollPos, 0);
      this.pathDatas.push(convertedPathData);

      // 再描画
      this.$_onPaint();
    },

    /**
     * タッチ位置の文字列取得（タッチ用）
     *
     * @param pathData 筆跡情報
     */
    $_getTouchText(): string {
      // 解答側の場合は筆跡開始位置をチェックして、回答欄の場合は空文字を返却
      if (this.isQuestion) {
        for (let i = 0; i < this.replys.length; i++) {
          if (this.replys[i].hitTest(this.pathData.pointX[0], this.pathData.pointY[0])) {
            return '';
          }
        }
      }

      const touchString1 = this.$_searchTouchText(this.pathData.pointX[0], this.pathData.pointY[0]);
      const touchString2 = this.$_searchTouchText(
        this.pathData.pointX[this.pathData.pointCount - 1],
        this.pathData.pointY[this.pathData.pointCount - 1]
      );
      if (touchString1 === touchString2) return touchString1;

      return '';
    },


    /**
     * タッチ位置の文字列を取得します
     * @param x Ｘ座標
     * @param y Ｙ座標
     */
    $_searchTouchText(x: number, y: number): string {

      // 問題が存在しないときは終了
      if (this.questionIndex < 0) return '';

      let keepFlag = false; // 抽出中フラグ
      let retString = '';

      let posR = this.margin; // 右端からの座標
      for (let i = 0; i < this.lines.length; i++) {
        const lineData = this.lines[i];
        let posY = this.margin; // 上からの座標
        posY += 3; // 文字と文字の境界をタッチした際に下の文字が拾われる感じがするので微調整

        for (let j = 0; j < lineData.wordList.length; j++) {
          const wordData = lineData.wordList[j];
          if (wordData.reply !== null) {
            // 文字列抽出中に入力欄があったら、それまでを返却
            if (keepFlag) return retString;
          } else if (wordData.image !== null) {
            // 文字列抽出中に画像があったら、それまでを返却
            if (keepFlag) return retString;
          } else {
            if (keepFlag) {
              retString += wordData.word;
              if (retString.length >= 10) return retString;
            } else {
              // 座標を左上からに直す
              const x1 = this.canvas.width - (posR + lineData.w - this.scrollPos);
              const x2 = x1 + lineData.w;

              const wordCount = wordData.word.length;
              const th = this.fontSize;
              let y1 = posY + (wordData.h - th * wordCount) / 2;
              for (let k = 0; k < wordCount; k++) {
                const y2 = y1 + th;
                if (x1 < x && x < x2 && y1 < y && y< y2) {
                  keepFlag = true;
                  retString = wordData.word.substring(k);
                  break;
                }
                y1 = y2;
              }
            }
          }
          posY += wordData.h;
        }
        posR += (lineData.w + this.lineSpace);
      }
      return retString;
    },

    /**
     * 次のフォーカス対象のインデックスを取得します。
     * 対象がない場合は-1を返却します。
     *
     * @return 次のフォーカス対象のインデックス
     */
    $_getNextFocusIndex(): number {
      let targetIndex = -1;

      // まずは以降の未解答欄を探しに行く
      for (let i = this.focusReplyViewIndex; i < this.replys.length; i++) {
        if (this.replys[i].getText() === '') {
          targetIndex = i;
          break;
        }
      }
      // 以降の未解答欄がない場合は先頭から探す
      if (targetIndex === -1) {
        for (let i = 0; i < this.focusReplyViewIndex; i++) {
          if (this.replys[i].getText() === '') {
            targetIndex = i;
            break;
          }
        }
      }

      return targetIndex;
    },

    /**
     * 連続学習用にキャンバスをクリアします
     */
    $_clearCanvas(): void {
      this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.enableLeftScroll = false;
      this.enableRightScroll = false;
    },

    $_animation(): void {
      if (this.questionIndex === -1) return; // 描画対象外の場合はreturn

      const orgScrollPos = this.scrollPos;
      if (this.isScrollingLeft) {
        this.scrollPos += 40;
        if (this.scrollPos > this.scrollWidth - this.canvas.width) {
          this.scrollPos = this.scrollWidth - this.canvas.width;
          this.isScrollingLeft = false;
        }
      }
      else if (this.isScrollingRight) {
        this.scrollPos -= 40;
        if (this.scrollPos < 0) {
          this.scrollPos = 0;
          this.isScrollingRight = false;
        }
      }

      // ボタン表示制御（canvas有効になる前に呼ばれてしまうのでcomputedは不可）
      this.enableLeftScroll = this.scrollPos < this.scrollWidth - this.canvas.width;
      this.enableRightScroll = this.scrollPos > 0;

      // 再描画
      if (this.scrollPos !== orgScrollPos) this.$_onPaint();
    },

  }

});
